import { MapStatusResponse } from '../services/liveMatchService';

/**
 * Determines if a match is BO3 (Best of 3) format
 *
 * Priority order:
 * 1. Use MapStatusResponse from API (most reliable - checks total number of maps)
 * 2. Check match.bo3 boolean field if available
 * 3. Fallback to round number heuristic (specific rounds are known to be BO3)
 * 4. Default to false (BO1)
 *
 * @param match - Match object with optional bo3 and roundNumber fields
 * @param mapStatus - Optional map status response from the API
 * @returns true if match is BO3, false if BO1
 */
export const isBO3Match = (
  match: { bo3?: boolean; roundNumber?: number | null },
  mapStatus?: MapStatusResponse | null
): boolean => {
  // Priority 1: Use map status from API (most reliable)
  if (mapStatus !== null && mapStatus !== undefined) {
    return mapStatus.totalMaps > 1;
  }

  // Priority 2: Check if match has explicit bo3 field
  if (match.bo3 !== undefined && match.bo3 !== null) {
    return match.bo3;
  }

  // Priority 3: Fallback to round number heuristic
  // These specific round numbers are known to be BO3 matches
  if (match.roundNumber !== undefined && match.roundNumber !== null) {
    const bo3RoundNumbers = [5, 6, 9, 10, 11, 12, 13, 14];
    return bo3RoundNumbers.includes(match.roundNumber);
  }

  // Default: assume BO1
  return false;
};

/**
 * Gets the match format string for display
 * @param match - Match object
 * @param mapStatus - Optional map status response from the API
 * @returns 'BO3' or 'BO1'
 */
export const getMatchFormat = (
  match: { bo3?: boolean; roundNumber?: number | null },
  mapStatus?: MapStatusResponse | null
): string => {
  return isBO3Match(match, mapStatus) ? 'BO3' : 'BO1';
};

/**
 * Determines if a match is BO3 for live matches
 * Uses additional heuristics based on live match state
 *
 * @param match - Live match object
 * @param mapStatus - Optional map status response from the API
 * @returns true if match is BO3
 */
export const isBO3LiveMatch = (
  match: {
    bo3?: boolean;
    roundNumber?: number | null;
    status?: string;
    map_number?: number;
    team1_series_score?: number;
    team2_series_score?: number;
  },
  mapStatus?: MapStatusResponse | null
): boolean => {
  // Use map status if available (most reliable)
  if (mapStatus !== null && mapStatus !== undefined) {
    return mapStatus.totalMaps > 1;
  }

  // Fallback to heuristics based on live match state
  // If status is 'pending_next_map', there must be multiple maps (BO3)
  if (match.status === 'pending_next_map') {
    return true;
  }

  // If currently on map 2 or 3, it's definitely BO3
  if (match.map_number && match.map_number > 1) {
    return true;
  }

  // If either team has won a map (series score > 0), it's BO3
  if ((match.team1_series_score && match.team1_series_score > 0) ||
      (match.team2_series_score && match.team2_series_score > 0)) {
    return true;
  }

  // Fall back to standard BO3 detection
  return isBO3Match(match, mapStatus);
};
